import type { CSSInterpolation } from '@emotion/serialize';
import facepaint from 'facepaint';
import { useCallback, useSyncExternalStore } from 'react';

import * as styles from './media-queries.module.scss';

type Facepaint = (...styles: any[]) => CSSInterpolation[];

const memoiseMatchMedia = (query: string): (() => MediaQueryList) => {
  let cached: MediaQueryList | undefined;
  let cachedMatchMedia: typeof window.matchMedia | undefined;
  return () => {
    if (
      !cached ||
      cachedMatchMedia !== window.matchMedia // Useful for tests
    ) {
      cached = window.matchMedia(query);
      cachedMatchMedia = window.matchMedia;
    }
    return cached;
  };
};

export const matchSmallTablet = memoiseMatchMedia(styles.smallTabletQuery);
export const matchLargeTablet = memoiseMatchMedia(styles.largeTabletQuery);
export const matchDesktop = memoiseMatchMedia(styles.desktopQuery);
export const matchHeaderDesktop = memoiseMatchMedia(styles.headerDesktopQuery);
export const matchLargeDesktop = memoiseMatchMedia(styles.largeDesktopQuery);
export const matchReducedMotion = memoiseMatchMedia(styles.reducedMotionQuery);
export const matchLandscape = memoiseMatchMedia(styles.landscapeQuery);
export const matchPortrait = memoiseMatchMedia(styles.portraitQuery);
export const matchHover = memoiseMatchMedia(styles.hoverQuery);

const subscribeToMediaQueryList = (
  getMediaQueryList: () => MediaQueryList,
  onChange: () => void
): (() => void) => {
  const mediaQueryList = getMediaQueryList();
  if (!mediaQueryList.addEventListener) {
    // Safari <14 / older WebKit only expose the deprecated `addListener` API.
    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }
  mediaQueryList.addEventListener('change', onChange);
  return () => mediaQueryList.removeEventListener('change', onChange);
};

export const useMatchMedia = (
  getMediaQueryList: () => MediaQueryList,
  serverSnapshot: boolean
): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => subscribeToMediaQueryList(getMediaQueryList, onChange),
    [getMediaQueryList]
  );
  return useSyncExternalStore(
    subscribe,
    () => getMediaQueryList().matches,
    () => serverSnapshot
  );
};

const atMedia = (query: string) => `@media ${query}`;

const mqDesktop: Facepaint = facepaint([atMedia(styles.desktopQuery)]);
const mqSmallTabletDesktop: Facepaint = facepaint([
  atMedia(styles.smallTabletQuery),
  atMedia(styles.desktopQuery),
]);
const mqLargeTabletDesktop: Facepaint = facepaint([
  atMedia(styles.largeTabletQuery),
  atMedia(styles.desktopQuery),
]);
const mqLargeDesktop: Facepaint = facepaint([
  atMedia(styles.largeDesktopQuery),
  atMedia(styles.extraLargeDesktopQuery),
]);

export const mq = Object.assign(mqDesktop, {
  withSmallTablet: mqSmallTabletDesktop,
  withLargeTablet: mqLargeTabletDesktop,
  withLargeDesktop: mqLargeDesktop,
});

export const mqReducedMotion: Facepaint = facepaint([atMedia(styles.reducedMotionQuery)]);

export default mq;
